import React, { useEffect, useState } from "react";
import { useTonConnectUI } from "@tonconnect/ui-react";

import SmallLogo from "../assets/logo/small-logo.svg";
import MoneyIcon from "../assets/icons/money-icon.svg";
import RefGroupIcon from "../assets/icons/refs-group-icon.svg";
import TicketIcon from "../assets/icons/ticket-pink-icon.svg";
import PriseIcon from "../assets/icons/prise-icon.svg";
import { PlayerBasicInfoDTO } from "../blockchain/lotteryClient";
import {
    getLevelK,
    getLevelPricesNano,
    getPlayerBasicInfo,
    getPlayerConsecutiveWins,
} from "../blockchain/lotteryClient";
import { useTonWallet } from "@tonconnect/ui-react";
import {
    REFERRAL_MILESTONE_FRIENDS,
    WIN_STREAK_LENGTH,
    referralMilestoneBonusNano,
    soldBonusPercent,
    winStreakBonusNano,
} from "../constants/lotteryRules";
import { formatGram } from "../utils/gram";

const GAME_LEVELS = [0, 1, 2];

function nextSoldBonusTier(ticketsSoldByReferrals: number): number | null {
    if (ticketsSoldByReferrals <= 10) return 10;
    if (ticketsSoldByReferrals <= 25) return 25;
    return null;
}


function Quests() {

    const [player, setPlayer] = useState<PlayerBasicInfoDTO | null>(null);
    const [loading, setLoading] = useState(false);
    const [levelPricesNano, setLevelPricesNano] = useState<bigint[] | null>(null);
    const [basePriceNano, setBasePriceNano] = useState<bigint | null>(null);
    const [bestWinStreak, setBestWinStreak] = useState(0);
    const wallet = useTonWallet();
    const address = wallet?.account.address;

    // Reward amounts depend on ticket prices, which the contract owner can change.
    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const [prices, regularK] = await Promise.all([
                    getLevelPricesNano(GAME_LEVELS),
                    getLevelK(0),
                ]);
                if (cancelled) return;
                setLevelPricesNano(prices);
                setBasePriceNano(regularK > 0 ? prices[0] / BigInt(regularK) : null);
            } catch (error) {
                console.error("Failed to load ticket prices for quests:", error);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!address) {
            setBestWinStreak(0);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const streaks = await Promise.all(
                    GAME_LEVELS.map((level) => getPlayerConsecutiveWins(address, level))
                );
                if (!cancelled) setBestWinStreak(Math.max(0, ...streaks));
            } catch (error) {
                console.error("Failed to load win streaks:", error);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [address]);


    useEffect(() => {
        if (!address) {
            setPlayer(null);
            return;
        }

        let cancelled = false;

        (async () => {
            setLoading(true);
            try {
                const info = await getPlayerBasicInfo(address);
                if (!cancelled) setPlayer(info);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [address]);

    // ===== QUEST DATA =====
    const referrals = player?.referredCount ?? 0;
    const soldTickets = player?.ticketsSoldByReferrals ?? 0;
    const nextSoldTier = nextSoldBonusTier(soldTickets);

    const referralReward = basePriceNano !== null
        ? `${formatGram(referralMilestoneBonusNano(basePriceNano, player?.userLevel ?? 1))} GRAM`
        : "-- GRAM";
    const streakReward = levelPricesNano
        ? `${formatGram(winStreakBonusNano(levelPricesNano[0]))}–${formatGram(winStreakBonusNano(levelPricesNano[2]))} GRAM`
        : "-- GRAM";

    const referralEarned = Number(player?.referralBonus ?? 0n) / 1e9;
    const soldEarned = Number(player?.soldBonus ?? 0n) / 1e9;
    const streakEarned = Number(player?.inRowBonus ?? 0n) / 1e9;


    if (loading) {
        return <div className="quests">Загрузка...</div>;
    }

    return (
        <div className="quests">
            <div className="quests_image">
                <img src={SmallLogo} alt="logo" width="129" height="73" />
            </div>

            <span className="quests_title">Задания и Награды</span>
            <span className="quests_subtitle"> Выполняй задания и получай бонусы. Смарт-контракт выплачивает их сразу после выполнения условия.</span>

            <div className="cards-group">

                {/* 🔹 REFERRALS */}
                <div className="quests_card">
                    <div className="quests_card__header">
                        <div className="quests_card__header___left">
                            <img src={RefGroupIcon} alt="icon" width={64} height={64} />
                            <div className="quests_card__header___left____task">
                                <div className="quests_card__header___left____task_____title">
                                    Привлечь 10 <br /> рефералов
                                </div>
                                <div className="quests_card__header___left____task_____count">
                                    {referrals % REFERRAL_MILESTONE_FRIENDS}/{REFERRAL_MILESTONE_FRIENDS}
                                </div>
                            </div>
                        </div>
                        <div className="quests_card__header___right">
                            <img src={MoneyIcon} alt="icon" width={45} height={45} />
                            <span>{referralReward}</span>
                        </div>
                    </div>
                    <div className="quests_card__bottom">
                        <span className="quests_card__bottom___text">
                            Заработано: {referralEarned.toFixed(2)} GRAM
                        </span>
                    </div>
                </div>

                {/* 🔹 SOLD TICKETS */}
                <div className="quests_card">
                    <div className="quests_card__header">
                        <div className="quests_card__header___left">
                            <img src={TicketIcon} alt="icon" width={64} height={64} />
                            <div className="quests_card__header___left____task">
                                <div className="quests_card__header___left____task_____title">
                                    Билеты, купленные <br /> друзьями
                                </div>
                                <div className="quests_card__header___left____task_____count">
                                    {nextSoldTier ? `${soldTickets}/${nextSoldTier}` : soldTickets}
                                </div>
                            </div>
                        </div>
                        <div className="quests_card__header___right">
                            <img src={MoneyIcon} alt="icon" width={45} height={45} />
                            <span>{soldBonusPercent(soldTickets)}%</span>
                        </div>
                    </div>
                    <div className="quests_card__bottom">
                        <span className="quests_card__bottom___text">
                            Заработано: {soldEarned.toFixed(2)} GRAM
                        </span>
                    </div>
                </div>

                {/* 🔹 3 WINS IN A ROW */}
                <div className="quests_card">
                    <div className="quests_card__header">
                        <div className="quests_card__header___left">
                            <img src={PriseIcon} alt="icon" width={64} height={64} />
                            <div className="quests_card__header___left____task">
                                <div className="quests_card__header___left____task_____title">
                                    Победить {WIN_STREAK_LENGTH} <br /> раза подряд
                                </div>
                                <div className="quests_card__header___left____task_____count">
                                    {Math.min(bestWinStreak, WIN_STREAK_LENGTH)}/{WIN_STREAK_LENGTH}
                                </div>
                            </div>
                        </div>
                        <div className="quests_card__header___right">
                            <img src={MoneyIcon} alt="icon" width={45} height={45} />
                            <span>{streakReward}</span>
                        </div>
                    </div>
                    <div className="quests_card__bottom">
                        <span className="quests_card__bottom___text">
                            Заработано: {streakEarned.toFixed(2)} GRAM
                        </span>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default Quests;
